import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { FiArrowRight, FiMenu, FiX } from 'react-icons/fi';
import { Home } from './pages/Home';
import { Pricing } from './pages/Pricing';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { Privacy } from './pages/Privacy';
import { moduleCount } from './data/product';
import logoMark from './assets/images/logo-mark.png';

/**
 * Routes whose hero is the dark ink treatment. On these the navbar starts
 * transparent with light text; everywhere else it stays solid from the top,
 * otherwise white nav text would sit on a white page and vanish.
 */
const DARK_HERO_ROUTES = ['/', '/pricing', '/about', '/contact'];

/** Routes where the marketing chrome is hidden entirely. */
const BARE_ROUTES = ['/dashboard', '/admin'];

/* ------------------------------------------------------------------ *
 * Custom cursor - desktop only
 * ------------------------------------------------------------------ */

const CustomCursor: React.FC = () => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = React.useState(false);
  const [enabled, setEnabled] = React.useState(false);

  React.useEffect(() => {
    // Skip entirely on touch devices, small screens, and when the user has
    // asked for reduced motion - a spring-following dot is pure decoration.
    const check = () => {
      const fine = window.matchMedia('(pointer: fine)').matches;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setEnabled(fine && !reduce && window.innerWidth >= 1024);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  React.useEffect(() => {
    if (!enabled) return;

    const onMove = (e: MouseEvent) => setPosition({ x: e.clientX, y: e.clientY });
    const onOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      setIsHovering(Boolean(target.closest('a, button, .cursor-hover')));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseover', onOver);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <motion.div
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 z-[60] h-2.5 w-2.5 rounded-full bg-accent-400 mix-blend-difference"
        animate={{ x: position.x - 5, y: position.y - 5, scale: isHovering ? 2.2 : 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 28 }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 z-[60] h-8 w-8 rounded-full border border-primary-400 mix-blend-difference"
        animate={{ x: position.x - 16, y: position.y - 16, scale: isHovering ? 1.6 : 1 }}
        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
      />
    </>
  );
};

/* ------------------------------------------------------------------ *
 * Scroll restoration
 * ------------------------------------------------------------------ */

function ScrollToTop() {
  const { pathname, hash, key } = useLocation();

  React.useEffect(() => {
    // A hash link ('/#platform') must scroll to its section rather than the top.
    // The browser only does this for real page loads, so on client-side
    // navigation we have to do it ourselves - otherwise clicking "Platform"
    // while already on the home page appears to do nothing at all.
    if (hash) {
      // One frame's delay so the target section is mounted when we look for it.
      const raf = requestAnimationFrame(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: 0, behavior: 'auto' });
      });
      return () => cancelAnimationFrame(raf);
    }

    // 'auto' on navigation - a smooth scroll from the bottom of a long page
    // to the top of a new one just looks like a glitch.
    window.scrollTo({ top: 0, behavior: 'auto' });
    // `key` changes on every navigation, so clicking the same hash link twice
    // still re-scrolls instead of silently doing nothing the second time.
  }, [pathname, hash, key]);

  return null;
}

/* ------------------------------------------------------------------ *
 * Brand mark
 * ------------------------------------------------------------------ */

const Logo: React.FC<{ invert?: boolean }> = ({ invert = false }) => (
  <Link to="/" className="group flex items-center gap-2.5" aria-label="BuildFlow home">
    <img
      src={logoMark}
      alt=""
      width={32}
      height={32}
      className="h-8 w-8 flex-none rounded-[9px] transition-transform duration-300 group-hover:scale-110"
    />
    <span
      className={`font-display text-xl font-bold tracking-tight ${
        invert ? 'text-white' : 'text-ink-800'
      }`}
    >
      Build<span className="text-gradient">Flow</span>
    </span>
  </Link>
);

/* ------------------------------------------------------------------ *
 * Navbar
 * ------------------------------------------------------------------ */

const NAV_LINKS = [
  { name: 'Platform', path: '/#platform' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the drawer on navigation, and never leave the body scroll-locked.
  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  if (BARE_ROUTES.includes(location.pathname)) return null;

  const overDarkHero = DARK_HERO_ROUTES.includes(location.pathname);
  const solid = isScrolled || !overDarkHero;

  return (
    <>
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          solid
            ? 'border-b border-slate-200/80 bg-white/90 py-3 shadow-sm backdrop-blur-xl'
            : 'border-b border-transparent py-5'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between gap-6">
            <Logo invert={!solid} />

            {/* Desktop links */}
            <div className="hidden items-center gap-8 md:flex">
              {NAV_LINKS.map((link) => {
                const active = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative text-sm font-medium transition-colors ${
                      solid
                        ? active
                          ? 'text-primary-600'
                          : 'text-slate-600 hover:text-primary-600'
                        : active
                        ? 'text-white'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {link.name}
                    {active && (
                      <motion.span
                        layoutId="activeNav"
                        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                        className={`absolute -bottom-1.5 left-0 right-0 h-0.5 rounded-full ${
                          solid ? 'bg-primary-600' : 'bg-accent-400'
                        }`}
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Desktop actions */}
            <div className="hidden items-center gap-3 md:flex">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:from-primary-500 hover:to-accent-500 hover:shadow-glow"
              >
                Start free
                <FiArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {/* Mobile trigger - 44px target */}
            <button
              className={`-mr-2 flex h-11 w-11 items-center justify-center rounded-lg md:hidden ${
                solid ? 'text-ink-700' : 'text-white'
              }`}
              onClick={() => setIsMenuOpen((o) => !o)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            className="fixed inset-0 z-40 bg-ink-900 px-6 pt-24 md:hidden"
          >
            <div className="absolute inset-0 blueprint-grid opacity-40" aria-hidden />
            <div className="relative flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="block border-b border-white/5 py-4 font-display text-2xl font-semibold text-white transition-colors hover:text-accent-300"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.36 }}
                className="mt-8 flex flex-col gap-3"
              >
                <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="btn-primary w-full">
                  Start free
                  <FiArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>

              <p className="mt-8 text-xs text-slate-500">
                {moduleCount} modules · From ₹999/month
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Footer
 * ------------------------------------------------------------------ */

const FOOTER_COLUMNS: { title: string; links: { label: string; to: string; external?: boolean }[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Platform overview', to: '/#platform' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Book a demo', to: '/contact' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About us', to: '/about' },
      { label: 'Contact', to: '/contact' },
      { label: 'Privacy policy', to: '/privacy' },
    ],
  },
];

const Footer: React.FC = () => {
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  if (BARE_ROUTES.includes(location.pathname)) return null;

  return (
    <footer className="relative overflow-hidden bg-ink-950 pt-16 text-white">
      <div className="absolute inset-x-0 top-0 h-px hairline" aria-hidden />
      <div className="container-custom relative">
        <div className="grid gap-10 pb-12 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1.2fr]">
          {/* Brand */}
          <div>
            <Logo invert />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
              The ERP built for architecture and construction firms. {moduleCount} connected modules
              covering sales, delivery, workforce, procurement and finance.
            </p>
            <p className="mt-4 text-sm text-slate-400">buildflow.team@gmail.com</p>
          </div>

          {/* Link columns */}
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.external ? (
                      <a
                        href={l.to}
                        className="text-sm text-slate-300 transition-colors hover:text-accent-300"
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="text-sm text-slate-300 transition-colors hover:text-accent-300"
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Mini CTA */}
          <div>
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              Get started
            </h4>
            <p className="mb-4 text-sm text-slate-400">
              Plans from <span className="font-semibold text-white">₹999/month</span>. No card
              required to begin.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:border-accent-400/50 hover:bg-white/10"
            >
              Book a demo
              <FiArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-6 sm:flex-row">
          <p className="text-xs text-slate-500">
            © {currentYear} BuildFlow. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-xs text-slate-500 transition-colors hover:text-slate-300">
              Privacy
            </Link>
            <span className="text-xs text-slate-600">Made in India 🇮🇳</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ------------------------------------------------------------------ */

function App() {
  return (
    <Router>
      <ScrollToTop />
      <CustomCursor />
      <Toaster position="top-right" />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
        </Routes>
      </main>
      <Footer />
    </Router>
  );
}

export default App;
